import api from '../app/api';
import { initialProfiles } from '../constants/initialState';

export default function useAppHandlers({
  profileDispatch,
  incomeDispatch,
  expenseDispatch,
  debtDispatch,
  userDispatch,
  profiles,
  userData,
  selectedSort,
  initialIncome,
  initialExpense,
  initialDebt,
  setNewProfileName,
  setEditingProfile,
  setErrorShow,
  setErrorTitle,
  setErrorMessage,
  setLoading,
}) {

  function handleCreateProfile(name, income, expenses, debt, yearCalc) {
    profileDispatch({
      type: 'CREATE',
      name,
      income,
      expenses,
      debt,
      yearCalc,
    });
  }

  const sendDeleteUser = async () => {
    setLoading(true);
    await api.delete('/api/users')
    .then(response => {
      if (response.status === 202) {
        handleLogOut(response.data);
      } else {
        setErrorShow(true);
        setErrorTitle("User Deletion Failed");
        setErrorMessage("An error occurred while deleting your account. Please try again.");
      }
      setLoading(false);
    })
    .catch(error => {
      if (error.response?.status === 401 || error.response?.status === 403) {
        setErrorTitle("Your session has expired");
        setErrorMessage("Your session has expired, please log in again.");
        setErrorShow(true);
      } else {
        setErrorShow(true);
        setErrorTitle("User Deletion Failed");
        setErrorMessage("An error occurred while deleting your account. Please try again.");
      }
      setLoading(false);
    });
  }

  const sendDeleteProfile = async (profileId) => {
    setLoading(true);
    await api.delete('/api/profiles/' + profileId)
    .then(response => {
      if (response.status === 200) {
        handleSave(response.data);
      } else {
        setErrorShow(true);
        setErrorTitle("Profile Deletion Failed");
        setErrorMessage("An error occurred while deleting the profile. Please try again.");
      }
      setLoading(false);
    })
    .catch(error => {
      if (error.response?.status === 401 || error.response?.status === 403) {
        setErrorTitle("Your session has expired");
        setErrorMessage("Your session has expired, please log in again.");
        setErrorShow(true);
      } else {
        setErrorShow(true);
        setErrorTitle("Profile Deletion Failed");
        setErrorMessage("An error occurred while deleting the profile. Please try again.");
      }
      setLoading(false);
    });
  }

  function handleDeleteProfile(pName) {    
    const result = profiles.find(({ name }) => name === pName);
    const index = profiles.findIndex(({ name }) => name === pName);
    if (result.id !== '') {
      sendDeleteProfile(result.id);
      setNewProfileName('');
      setEditingProfile((s) => !s);
    } else {
      profileDispatch({
        type: 'REMOVE',
        profile: { name: pName },
      });
      setNewProfileName('');
      setEditingProfile((s) => !s);
      getProfile(index > 0 ? profiles[0].id : profiles[1]?.id);
    }
  }

  function handleSave(data) {
    incomeDispatch({
      type: 'ADD_SAVED_RECORDS',
      records: data.income,
      descriptions: data.descriptions.filter((d) => d.category === 'INCOME'),
    });
    expenseDispatch({
      type: 'ADD_SAVED_RECORDS',
      records: data.expenses,
      descriptions: data.descriptions.filter((d) => d.category === 'EXPENSE'),
    });
    const imt = data.income.map(item => +item.monthlyAmount).reduce((prev, next) => 
        prev + next);
    const emt = data.expenses.map(item => +item.monthlyAmount).reduce((prev, next) => 
        prev + next);
    debtDispatch({
      type: 'UPDATE_SAVED_DEBT',
      descriptions: data.descriptions.filter((d) => d.category === 'DEBT'),
      debt: data.debt,
      yearCalc: data.selectedProfile.yearCalc,
      remaining: imt - emt - (imt*0.02),
    });
    if (data.profiles !== undefined) {
      profileDispatch({ type: 'SAVE_ALL', profiles: data.profiles });
    }
    profileDispatch({
      type: 'SAVE',
      profile: {
        id: data.selectedProfile.id,
        name: data.selectedProfile.name,
        income: initialIncome,
        expenses: initialExpense,
        debt: initialDebt,
        yearCalc: data.selectedProfile.yearCalc,
      },
    });
    userDispatch({ type: 'UPDATE_SELECTED_PROFILE', profile: data.selectedProfile });
    debtDispatch({ type: 'SORT', sortBy: selectedSort });
  }

  function handleReloadSaved(id, name, income, expenses, debt, yearCalc) {
    incomeDispatch({ type: 'RELOAD', data: income });
    expenseDispatch({ type: 'RELOAD', data: expenses });
    debtDispatch({ type: 'RELOAD', data: debt });
    userDispatch({ type: 'UPDATE_SELECTED_PROFILE_ID', id });
    userDispatch({ type: 'UPDATE_SELECTED_PROFILE_NAME', name });
    userDispatch({ type: 'UPDATE_SELECTED_PROFILE_YEARCALC', yearCalc });
    debtDispatch({ type: 'SORT', sortBy: selectedSort });
  }

  function handleYearCalcSwitch() {
    profileDispatch({ type: 'YEAR_CALC_SWITCH', name: userData.selectedProfile.name });
    userDispatch({
      type: 'UPDATE_SELECTED_PROFILE_YEARCALC',
      yearCalc: !profiles.find(({ name }) => name === userData.selectedProfile.name).yearCalc,
    });
  }

  function handleLoggedIn() {
    userDispatch({ type: 'UPDATE_LOGGED_IN' });
  }

  function handleEmailUpdate(email) {
    userDispatch({ type: 'UPDATE_EMAIL', email });
  }

  function handleNameUpdate(name) {
    userDispatch({ type: 'UPDATE_NAME', name });
  }

  function handleSurnameUpdate(surname) {
    userDispatch({ type: 'UPDATE_SURNAME', surname });
  }

  function handleAddIncome(index) {
    incomeDispatch({ type: 'ADD', index });
  }

  function handleChangeIncome(index, event, yearCalc) {
    incomeDispatch({ type: 'CHANGE', event, index, yearCalc });
  }

  function handleUpdateIncome(index, event) {
    incomeDispatch({ type: 'UPDATE', event, index });
  }

  function handleRemoveIncome(index) {
    incomeDispatch({ type: 'REMOVE', index });
  }

  function handleCreateIncomeDescription(inputValue, index, category) {
    incomeDispatch({ type: 'CREATE_DESCRIPTION', inputValue, index, category });
  }

  function handleMfaEnabled(enabled) {
    userDispatch({ type: 'UPDATE_MFA_ENABLED', mfaEnabled: enabled });
  }

  function handleMfaType(type) {
    userDispatch({ type: 'UPDATE_MFA_TYPE', mfaType: type });
  }

  async function handleDisableMfa() {
    setLoading(true);
    try {
      const response = await api.get('/auth/mfa/setup/disable');
      if (response.status === 200) {
        handleMfaEnabled(false);
        handleMfaType('');
      } else {
        setErrorTitle('MFA could not be disabled');
        setErrorMessage('An error occurred while disabling multi-factor authentication. Please try again.');
        setErrorShow(true);
      }
    } catch (error) {
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        setErrorTitle('Your session has expired');
        setErrorMessage('Your session has expired. Please log in again.');
        setErrorShow(true);
        handleLogOut();
      } else {
        setErrorTitle('MFA could not be disabled');
        setErrorMessage('An error occurred while disabling multi-factor authentication. Please try again.');
        setErrorShow(true);
      }
    } finally {
      setLoading(false);
    }
  }

  function handleAddExpense(index) {
    expenseDispatch({ type: 'ADD', index });
  }

  function handleChangeExpense(index, event, yearCalc) {
    expenseDispatch({ type: 'CHANGE', event, index, yearCalc });
  }

  function handleUpdateExpense(index, event) {
    expenseDispatch({ type: 'UPDATE', event, index });
  }

  function handleRemoveExpense(index) {
    expenseDispatch({ type: 'REMOVE', index });
  }

  function handleCreateExpenseDescription(inputValue, index, category) {
    expenseDispatch({ type: 'CREATE_DESCRIPTION', inputValue, index, category });
  }

  function handleAutoWeekly() {
    debtDispatch({ type: 'OVERALL_WEEKLY' });
  }

  function handleRepaymentType(yearCalc, repaymentType, remaining, sortBy) {
    debtDispatch({ type: 'UPDATE_REPAYMENT_TYPE', yearCalc, repaymentType, remaining, sortBy});
  }

  function handleManualSwitch() {
    debtDispatch({ type: 'MANUAL_SWITCH' });
  }

  function handleAddAutoPayment(yearCalc, remaining, sortBy) {
    debtDispatch({ type: 'ADD_AUTO_PAYMENTS', yearCalc, remaining, sortBy});
  }

  function handleAddDebt(index) {
    debtDispatch({ type: 'ADD', index });
  }

  function handleChangeDebt(index, event, yearCalc, remaining, sortBy) {
    debtDispatch({ type: 'CHANGE', event, index, yearCalc, remaining, sortBy });
  }

  function handleChangeDebtPeriod(event, yearCalc, remaining) {
    debtDispatch({ type: 'CHANGE_PERIOD', value: event.target.value, yearCalc, remaining });
  }

  function handleUpdateDebt(index, event, yearCalc, remaining, sortBy) {
    debtDispatch({ type: 'UPDATE', event, index, yearCalc, remaining, sortBy });
  }

  function handleBlurUpdateDebt(index, event) {
    debtDispatch({ type: 'BLUR_UPDATE', event, index });
  }

  function handleRemoveDebt(index, yearCalc, remaining, sortBy) {
    debtDispatch({ type: 'REMOVE', index, yearCalc, remaining, sortBy });
  }

  function handleCreateDebtDescription(inputValue, index) {
    debtDispatch({ type: 'CREATE_DESCRIPTION', inputValue, index });
  }

  function handleChangeWeekly(index) {
    debtDispatch({ type: 'UPDATE_WEEKLY', index });
  }

  function handleAddTransaction(index, remaining, sortBy) {
    debtDispatch({ type: 'ADD_TRANSACTION', index, remaining, sortBy});
  }

  function handleChangeTransaction(event, index, jndex) {
    debtDispatch({ type: 'CHANGE_TRANSACTION', event, index, jndex });
  }

  function handleRemoveTransaction(index, jndex, remaining, sortBy) {
    debtDispatch({ type: 'REMOVE_TRANSACTION', index, jndex, remaining, sortBy });
  }

  function handleDebtSort(sortBy) {
    debtDispatch({ type: 'SORT', sortBy });
  }

  function handleDebtRefresh(yearCalc, remaining, sortBy) {
    debtDispatch({ type: 'REFRESH', yearCalc, remaining, sortBy });
  }

  const postLogOut = async () => {
    await api.post('/auth/logout', {});
  };

  function handleLogOut() {
    postLogOut();
    handleLoggedIn();
    handleEmailUpdate('');
    handleNameUpdate('');
    handleSurnameUpdate('');
    handleReloadSaved('', 'Default', initialIncome, initialExpense, initialDebt, true);
    profileDispatch({ type: 'CLEAR', initialProfiles: initialProfiles });
  }

  const getProfile = async (profileId) => {
    setLoading(true);
    try {
      const response = await api.get('/api/profiles/data/' + profileId);
      if (response.status === 200 && response.data.selectedProfile !== undefined) {
        handleSave(response.data);
      } else {
        setErrorShow(true);
        setErrorTitle("Profile Load Failed");
        setErrorMessage("An error occurred while loading the profile. Please try again.");
      }
    } catch (error) {
      console.error('Profile load failed:', error.response?.status);
      if (error.response?.status === 401 || error.response?.status === 403) {
        setErrorTitle("Your session has expired");
        setErrorMessage("Your session has expired, please log in again.");
        setErrorShow(true);
        handleLogOut();
      } else {
        setErrorTitle("Profile Load Failed");
        setErrorMessage("An error occurred while loading the profile. Please try again.");
        setErrorShow(true);
      }
    }
    setLoading(false);
  };

  return {
    sendDeleteUser,
    handleCreateProfile,
    sendDeleteProfile,
    handleDeleteProfile,
    handleSave,
    handleReloadSaved,
    handleYearCalcSwitch,
    handleLoggedIn,
    handleEmailUpdate,
    handleNameUpdate,
    handleSurnameUpdate,
    handleMfaEnabled,
    handleMfaType,
    handleDisableMfa,
    handleAddIncome,
    handleChangeIncome,
    handleUpdateIncome,
    handleRemoveIncome,
    handleCreateIncomeDescription,
    handleAddExpense,
    handleChangeExpense,
    handleUpdateExpense,
    handleRemoveExpense,
    handleCreateExpenseDescription,
    handleAutoWeekly,
    handleRepaymentType,
    handleManualSwitch,
    handleAddAutoPayment,
    handleAddDebt,
    handleChangeDebt,
    handleChangeDebtPeriod,
    handleUpdateDebt,
    handleBlurUpdateDebt,
    handleRemoveDebt,
    handleCreateDebtDescription,
    handleChangeWeekly,
    handleAddTransaction,
    handleChangeTransaction,
    handleRemoveTransaction,
    handleDebtSort,
    handleDebtRefresh,
    postLogOut,
    handleLogOut,
    getProfile,
  };
}
